// src/admin/pages/FormPreview.jsx
import { Beaker, Edit, ExternalLink, FileEdit } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FormRenderer from "../../public/components/FormRenderer";
import { PREFIX } from "../../router";

import { DRAFT_LOAD_FORM_URL } from "../../constants/urls";

const FormPreview = ({ mode = "form", subform = false }) => {
  const { formId } = useParams();
  const [formMetadata, setFormMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get mode-specific configuration
  const getModeConfig = () => {
    switch (mode) {
      case "test":
        return {
          title: "Test Form Preview",
          icon: Beaker,
          badge: "Test Environment",
          badgeColor: "yellow",
          basePath: `${PREFIX}/test`,
          editPath: `/admin/test/edit`,
          publicPath: "/test",
        };
      case "draft":
        return {
          title: "Draft Preview",
          icon: FileEdit,
          badge: "Draft",
          badgeColor: "blue",
          basePath: subform
            ? `${DRAFT_LOAD_FORM_URL}/subform`
            : DRAFT_LOAD_FORM_URL,
          editPath: subform
            ? `/admin/draft/edit/subforms`
            : `/admin/draft/edit`,
          publicPath: "/draft",
          loadMethod: "api",
        };
      default:
        return {
          title: "Form Preview",
          editPath: subform ? `/admin/form/edit/subforms` : `/admin/form/edit`,
          publicPath: "/form",
          basePath: subform ? `${PREFIX}/forms/subforms` : `${PREFIX}/forms`,
        };
    }
  };

  const config = getModeConfig();

  const loadForm = useCallback(async () => {
    if (!formId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let metadata;
      if (config.loadMethod === "api") {
        // Load draft form using the environment-specific URL
        const response = await fetch(`${config.basePath}/${formId}.json`);

        if (!response.ok) {
          throw new Error(`Draft not found (${response.status})`);
        }
        const data = await response.json();
        metadata = data.draft_data;
      } else {
        // Load regular or test form
        const response = await fetch(`${config.basePath}/${formId}.json`);
        if (!response.ok) {
          throw new Error(`Form not found (${response.status})`);
        }
        metadata = await response.json();
      }

      setFormMetadata(metadata);
    } catch (err) {
      console.error("Error loading form:", err);
      setError(
        "An error occurred while loading the form. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  }, [formId, config.basePath, config.loadMethod]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            Loading {config.title.toLowerCase()}...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{config.title}</h1>
          {config.badge && (
            <span
              className={`px-3 py-1 bg-${config.badgeColor}-100 text-${config.badgeColor}-800 rounded-full text-sm flex items-center gap-2`}
            >
              <config.icon size={16} />
              {config.badge}
            </span>
          )}
        </div>
        <div className="flex gap-4">
          <Link
            to={`${config.editPath}/${formId}`}
            className={`flex items-center gap-2 px-4 py-2 ${
              mode === "draft"
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "text-gray-700 hover:bg-gray-50"
            } rounded border border-gray-300`}
          >
            <Edit size={20} />
            Inspect
          </Link>
          <Link
            to={`${config.publicPath}/${formId}`}
            target="_blank"
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            <ExternalLink size={20} />
            Open Link
          </Link>
        </div>
      </div>

      <FormRenderer formMetadata={formMetadata} isPreview={true} mode={mode} />
    </div>
  );
};

export default FormPreview;
